/**
 * Validates and sanitizes a returnTo URL against allowed Peloton domains.
 *
 * Rules:
 * - Relative paths (starting with a single `/`) are always allowed
 * - https: is required for Peloton production hosts
 * - http: is allowed only for localhost / 127.0.0.1 (local dev)
 * - Hostname must be a subdomain of a supported Peloton TLD
 *   (e.g. *.onepeloton.com, *.onepeloton.co.uk). The bare apex
 *   (e.g. `onepeloton.com`) is intentionally rejected — we require
 *   an explicit subdomain like `www.` or `account.` to avoid accepting
 *   redirects to an unclaimed apex host
 * - Same-origin URLs are always allowed (covers preview/ephemeral envs)
 * - Returns the fallback URL if validation fails
 *
 * This replaces the divergent sanitization approaches:
 * - Members: `safelyDecode` + `isAllowedEnv` check
 * - Account: `strictDomainSanitize` from @core/external-links
 * - next-www: `btoa`/`atob` + no real validation
 */

import { SupportedTLD } from '@core/internationalize/models/locale';

// SupportedTLD values are e.g. '.com', '.co.uk', '.ca', '.com.au', '.de', '.at'.
const PELOTON_HOST_SUFFIXES = Object.values(SupportedTLD).map(tld => `.onepeloton${tld}`);

const isLocalhost = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1';

/**
 * Checks if a hostname belongs to a Peloton domain.
 * Accepts any subdomain of a supported Peloton TLD (see SupportedTLD),
 * localhost (for local dev), and same-origin URLs.
 */
const isPelotonOrigin = (url: URL): boolean => {
  const { hostname } = url;

  if (typeof window !== 'undefined' && url.origin === window.location.origin) {
    return true;
  }

  if (isLocalhost(hostname)) {
    return true;
  }

  return PELOTON_HOST_SUFFIXES.some(suffix => hostname.endsWith(suffix));
};

export const sanitizeReturnTo = (rawUrl: string, fallbackUrl: string): string => {
  // Empty or missing — use fallback
  if (!rawUrl || rawUrl.trim() === '') {
    return fallbackUrl;
  }

  // Path-like inputs (anything starting with `/`) must never be parsed as
  // absolute URLs — backslashes are normalised to `/` by `new URL`, which would
  // turn `/foo\\bar.com` into `https://<base>/foo/bar.com` and pass the
  // same-origin check. Reject backslash-containing path inputs outright.
  if (rawUrl.startsWith('/')) {
    if (rawUrl.startsWith('//') || rawUrl.includes('\\')) {
      return fallbackUrl;
    }
    return rawUrl;
  }

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : undefined;
    const url = new URL(rawUrl, base);

    const isAllowedProtocol =
      url.protocol === 'https:' ||
      (url.protocol === 'http:' && isLocalhost(url.hostname));

    if (!isAllowedProtocol) {
      return fallbackUrl;
    }

    if (!isPelotonOrigin(url)) {
      return fallbackUrl;
    }

    return url.href;
  } catch {
    return fallbackUrl;
  }
};
